import type { ZoomTransform } from 'd3-zoom';
import { zoomIdentity, zoomTransform } from 'd3-zoom';

import type {
  BubbleSvgSelection,
  BubbleZoomBehavior,
} from '../../../lib/d3/d3SelectionTypes';
import type { BubbleRenderOptions, BubbleZoomControls } from './bubbleRenderOptions';

const zoomCacheMax = 20;

/** Target on-screen diameter for the smallest volume at max zoom. */
export const bubbleMaxZoomVolumeDiameterPx = 56;

/** Padding (viewBox units) when fitting all content. */
export const bubbleFitPaddingVb = 8;

/** Absolute ceiling so empty/odd trees cannot explode max zoom. */
export const bubbleScaleKMaxCeiling = 80;

/** Floor for max-zoom-out so the canvas never becomes unusably tiny. */
export const bubbleScaleKMinFloor = 0.25;

const zoomTransformCache = new Map<string, ZoomTransform>();
const lastRenderZoomKeyByContainer = new WeakMap<HTMLElement, string>();

export function zoomCacheKey(options: BubbleRenderOptions, w: number, h: number) {
  return [
    options.view,
    options.canvasId,
    options.drillPath.join('/'),
    options.groupByAccount ? '1' : '0',
    options.secondary,
    options.tagKey ?? '',
    `${w}x${h}`,
  ].join('|');
}

export function setZoomCache(key: string, transform: ZoomTransform) {
  if (zoomTransformCache.has(key)) zoomTransformCache.delete(key);
  zoomTransformCache.set(key, transform);
  while (zoomTransformCache.size > zoomCacheMax) {
    const oldest = zoomTransformCache.keys().next().value;
    if (oldest === undefined) break;
    zoomTransformCache.delete(oldest);
  }
}

export function deleteZoomCache(key: string) {
  zoomTransformCache.delete(key);
}

export function getSavedZoomTransform(key: string) {
  return zoomTransformCache.get(key);
}

export function snapshotZoomBeforeRender(container: HTMLElement) {
  const existingSvg = container.querySelector('svg');
  const lastRenderZoomKey = lastRenderZoomKeyByContainer.get(container);
  if (existingSvg && lastRenderZoomKey) {
    setZoomCache(lastRenderZoomKey, zoomTransform(existingSvg));
  }
}

export function rememberRenderZoomKey(container: HTMLElement, key: string) {
  lastRenderZoomKeyByContainer.set(container, key);
}

/** d3-zoom filter: left or right drag pans; the wheel always zooms via `wheel.bubbleZoom`. */
export function bubbleZoomDragFilter(event: {
  type: string;
  button?: number;
  ctrlKey?: boolean;
}): boolean {
  if (event.type === 'wheel') return false;
  const button = event.button ?? 0;
  return button === 0 || button === 2;
}

export type BubbleScaleExtentInput = {
  viewBoxSize: number;
  rootRadius: number;
  rootX: number;
  rootY: number;
  /** Smallest packed volume radius in viewBox units; null when none rendered. */
  minVolumeRadius: number | null;
  /** viewBoxSize / min(containerW, containerH) — maps viewBox → CSS px at k=1. */
  invFit: number;
  targetVolumeScreenDiameterPx?: number;
  fitPadding?: number;
};

export type BubbleScaleExtent = {
  kMin: number;
  kMax: number;
  fitTransform: ZoomTransform;
};

/** Minimum zoom-in scale relative to fit (k=1), so large drilled volumes can still zoom. */
export const bubbleScaleKMaxFloor = 4;

/**
 * Max zoom-out shows all content (with padding); Fit resets to k=1 (100%).
 * Max zoom-in reaches a readable size for the smallest volume, and is at least
 * {@link bubbleScaleKMaxFloor}× so drilled-in large volumes can still zoom.
 */
export function computeBubbleScaleExtent(input: BubbleScaleExtentInput): BubbleScaleExtent {
  const {
    viewBoxSize,
    rootRadius,
    rootX,
    rootY,
    minVolumeRadius,
    invFit,
    targetVolumeScreenDiameterPx = bubbleMaxZoomVolumeDiameterPx,
    fitPadding = bubbleFitPaddingVb,
  } = input;

  const available = Math.max(1, viewBoxSize - 2 * fitPadding);
  const diameter = Math.max(1, 2 * Math.max(rootRadius, 1));
  // Pack layout already fills the viewBox at k=1; allow a little zoom-out for padding.
  const kMin = Math.max(bubbleScaleKMinFloor, Math.min(1, available / diameter));
  const kFit = 1;

  let kMax: number;
  if (minVolumeRadius != null && minVolumeRadius > 0 && invFit > 0) {
    // screenDiameter = (2 * r * k) / invFit
    kMax = (targetVolumeScreenDiameterPx * invFit) / (2 * minVolumeRadius);
  } else {
    kMax = 25;
  }
  kMax = Math.min(
    bubbleScaleKMaxCeiling,
    Math.max(bubbleScaleKMaxFloor, kMin * 1.01, kMax)
  );

  // Fit = 100%: content as packed (identity scale), centered on the root.
  const fitTransform = zoomIdentity
    .translate(viewBoxSize / 2 - kFit * rootX, viewBoxSize / 2 - kFit * rootY)
    .scale(kFit);

  return { kMin, kMax, fitTransform };
}

export function zoomCanZoomIn(k: number, kMax: number, epsilon = 1e-3): boolean {
  return k < kMax - epsilon;
}

export function zoomCanZoomOut(k: number, kMin: number, epsilon = 1e-3): boolean {
  return k > kMin + epsilon;
}

export function clampZoomTransform(
  transform: ZoomTransform,
  kMin: number,
  kMax: number
): ZoomTransform {
  const k = Math.max(kMin, Math.min(kMax, transform.k));
  if (k === transform.k) return transform;
  return zoomIdentity.translate(transform.x, transform.y).scale(k);
}

/** Scale factor for a custom wheel zoom step (mirrors d3-zoom default wheelDelta intent). */
export function wheelZoomFactor(event: {
  deltaMode?: number;
  deltaY?: number;
}): number {
  const deltaMode = event.deltaMode ?? 0;
  const deltaY = event.deltaY ?? 0;
  return Math.pow(2, -deltaY * (deltaMode === 1 ? 0.05 : deltaMode ? 1 : 0.002));
}

export function createBubbleZoomControls(
  bubbleZoom: BubbleZoomBehavior | null,
  bubbleSvg: BubbleSvgSelection | null,
  options?: {
    onZoomFit?: () => void;
    fitTransform?: ZoomTransform;
  }
): BubbleZoomControls {
  const onZoomFit = options?.onZoomFit;
  const fitTransform = options?.fitTransform ?? zoomIdentity;

  return {
    zoomIn: () => {
      if (!bubbleZoom || !bubbleSvg) return;
      bubbleZoom.scaleBy(bubbleSvg.transition().duration(180), 1.4);
    },
    zoomOut: () => {
      if (!bubbleZoom || !bubbleSvg) return;
      bubbleZoom.scaleBy(bubbleSvg.transition().duration(180), 1 / 1.4);
    },
    zoomFit: () => {
      if (!bubbleZoom || !bubbleSvg) return;
      onZoomFit?.();
      bubbleZoom.transform(bubbleSvg.transition().duration(220), fitTransform);
    },
  };
}
